'use client';

import {
  BookOpenIcon,
  BrainIcon,
  CaretDownIcon,
  SignOutIcon,
  UserIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { Badge, MENU_ROW_CLASS, MENU_SURFACE_CLASS } from '@/features/navbar/lib/header-constants';
import { cn } from '@/shared/utils/cn';

const DEFAULT_USER_NAME = 'Mi cuenta';
const DEFAULT_CLASSROOM_URL = '/mi-aula';
const DEFAULT_PROFILE_URL = '/mi-perfil';
const DEFAULT_LAB_URL = '/adipa-lab';

export interface UserMenuLink {
  href: string;
  label: string;
  icon: 'book' | 'user' | 'brain';
  badge?: string;
  badgeColor?: 'green' | 'magenta' | 'brand';
}

export interface UserMenuWidgetProps {
  /** Nombre del usuario; el trigger muestra solo el primer nombre. */
  userName?: string;
  /** URL del avatar; con error de carga o sin URL cae al ícono de usuario. */
  userAvatarUrl?: string;
  /** Destino de "Mi aula". */
  classroomUrl?: string;
  /** Badge opcional junto a "Mi aula" (p. ej. cantidad de cursos nuevos). */
  classroomBadge?: string;
  /** Destino de "Mi perfil". */
  profileUrl?: string;
  /** Destino de "Adipa Lab". */
  labUrl?: string;
  /** Si está definida, "Cerrar sesión" navega a esta URL tras onLogoutClick. */
  logoutUrl?: string;
  /** Callback al hacer click en un item del menú (además de la navegación). */
  onItemClick?: (link: UserMenuLink) => void;
  /** Callback al hacer click en "Cerrar sesión". */
  onLogoutClick?: () => void | Promise<void>;
}

const ICON_MAP: Record<UserMenuLink['icon'], PhosphorIcon> = {
  book: BookOpenIcon,
  brain: BrainIcon,
  user: UserIcon,
};

export function buildMenuLinks({
  classroomBadge,
  classroomUrl = DEFAULT_CLASSROOM_URL,
  labUrl = DEFAULT_LAB_URL,
  profileUrl = DEFAULT_PROFILE_URL,
}: Pick<
  UserMenuWidgetProps,
  'classroomBadge' | 'classroomUrl' | 'labUrl' | 'profileUrl'
>): UserMenuLink[] {
  return [
    {
      href: classroomUrl,
      icon: 'book',
      label: 'Mi aula',
      ...(classroomBadge ? { badge: classroomBadge, badgeColor: 'magenta' as const } : {}),
    },
    { href: profileUrl, icon: 'user', label: 'Mi perfil' },
    { href: labUrl, icon: 'brain', label: 'Adipa Lab' },
  ];
}

export function UserMenuWidget({
  classroomBadge,
  classroomUrl,
  labUrl,
  logoutUrl,
  onItemClick,
  onLogoutClick,
  profileUrl,
  userAvatarUrl,
  userName = DEFAULT_USER_NAME,
}: UserMenuWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAvatarError(false);
  }, [userAvatarUrl]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    if (onLogoutClick) await onLogoutClick();
    if (logoutUrl && typeof window !== 'undefined') {
      window.location.href = logoutUrl;
    }
  };

  const handleItemClick = (link: UserMenuLink) => {
    setIsOpen(false);
    onItemClick?.(link);
  };

  const menuLinks = buildMenuLinks({ classroomBadge, classroomUrl, labUrl, profileUrl });
  const firstName = userName.split(' ')[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'rounded-adipa-lg text-adipa-text-primary flex items-center gap-2 px-2.5 py-1.5 text-sm font-semibold',
          'duration-adipa-fast transition-colors ease-out motion-reduce:transition-none',
          'hover:bg-adipa-surface-lavender active:bg-adipa-surface-lavender',
          'focus-visible:outline-adipa-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span
          aria-hidden="true"
          className="rounded-adipa-full border-adipa-border shadow-adipa-card flex h-7 w-7 shrink-0 items-center justify-center border bg-white p-0.5"
        >
          {userAvatarUrl && !avatarError ? (
            <Image
              alt=""
              className="rounded-adipa-full h-full w-full object-cover"
              height={24}
              src={userAvatarUrl}
              unoptimized
              width={24}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span className="rounded-adipa-full bg-adipa-surface-muted text-adipa-primary flex h-full w-full items-center justify-center">
              <UserIcon size={15} weight="regular" />
            </span>
          )}
        </span>
        <span className="max-w-28 truncate">{firstName}</span>
        <CaretDownIcon
          aria-hidden="true"
          className={cn(
            'duration-adipa-fast shrink-0 transition-transform ease-out',
            isOpen && 'rotate-180',
          )}
          size={14}
          weight="bold"
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className={cn(
              'absolute right-0 top-full z-50 mt-2 w-52 origin-top-right',
              MENU_SURFACE_CLASS,
            )}
            exit={{ opacity: 0, scale: 0.97, y: -6 }}
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            role="menu"
            transition={{ bounce: 0, duration: 0.2, type: 'spring' }}
          >
            {menuLinks.map((link) => {
              const Icon = ICON_MAP[link.icon];
              return (
                <Link
                  className={cn(MENU_ROW_CLASS, 'flex items-center gap-2.5')}
                  href={link.href}
                  key={link.href}
                  role="menuitem"
                  onClick={() => handleItemClick(link)}
                >
                  <Icon aria-hidden="true" size={16} weight="regular" />
                  {link.label}
                  {link.badge ? (
                    <Badge color={link.badgeColor ?? 'magenta'} label={link.badge} />
                  ) : null}
                </Link>
              );
            })}
            <div aria-hidden="true" className="border-adipa-border my-1 border-t" />
            <button
              className={cn(
                MENU_ROW_CLASS,
                'text-adipa-error hover:bg-adipa-error-surface hover:text-adipa-error flex items-center gap-2.5',
              )}
              role="menuitem"
              type="button"
              onClick={handleLogout}
            >
              <SignOutIcon aria-hidden="true" size={16} weight="regular" />
              Cerrar sesión
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
